import React from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FloatingLabelInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components";
import { useI18n } from "@/context/i18n";

import type { IUseWhatsappMetaConfig } from "../../whatsapp-meta-config.hooks.interface";

export interface IWhatsappMetaConfigFormDialogProps {
  hook: Pick<
    IUseWhatsappMetaConfig,
    | "dialogOpen"
    | "editingId"
    | "form"
    | "closeDialog"
    | "onSubmit"
    | "isSaving"
  >;
}

export const WhatsappMetaConfigFormDialog: React.FC<
  IWhatsappMetaConfigFormDialogProps
> = ({ hook }) => {
  const { t } = useI18n();
  const { dialogOpen, editingId, form, closeDialog, onSubmit, isSaving } = hook;

  return (
    <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="flex max-h-[min(90vh,640px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <div className="flex flex-col gap-4 overflow-y-auto px-6 pb-4 pt-6">
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="pr-10 text-xl font-semibold leading-tight">
              {editingId
                ? t("campaigns.whatsappMeta.edit")
                : t("campaigns.whatsappMeta.add")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
            <p>{t("campaigns.whatsappMeta.dialogIntro")}</p>
            {editingId ? (
              <p>{t("campaigns.whatsappMeta.tokenOptionalOnEdit")}</p>
            ) : null}
          </div>
          <Form {...form} key={editingId ?? "create"}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-5"
            >
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormControl>
                      <FloatingLabelInput
                        label={t("campaigns.whatsappMeta.fields.label")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="waba_id"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormControl>
                      <FloatingLabelInput
                        label={t("campaigns.whatsappMeta.fields.wabaId")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="access_token"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormControl>
                      <FloatingLabelInput
                        label={t("campaigns.whatsappMeta.fields.accessToken")}
                        placeholder=" "
                        type="password"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-1 flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:justify-end sm:gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full shrink-0 sm:w-auto"
                  onClick={closeDialog}
                >
                  {t("campaigns.whatsappMeta.cancel")}
                </Button>
                <Button
                  type="submit"
                  className="w-full min-w-[8rem] shrink-0 sm:w-auto"
                  disabled={isSaving}
                >
                  {t("campaigns.whatsappMeta.save")}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
